import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePrevious } from "react-use";
import { useDebouncedValue } from "@mantine/hooks";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";

import { DEFAULT_PAGINATION, QP } from "@constants/index";

import { Pagination } from "@customTypes/index";

// ! This is deprecated along with CustomPagination component, and should be removed after replacing all instances
interface SyncTableQueryParamsProps {
  searchFilter?: string;
  statusFilter?: string;
  doctorFilter?: string;
}

export const useSyncTableQueryParams = ({
  searchFilter = "",
  statusFilter = "",
  doctorFilter = "",
}: SyncTableQueryParamsProps) => {
  const { search: queryParams } = useLocation();
  const [page, setPage] = useQueryParam(QP.PAGE, withDefault(NumberParam, 0));

  const [debounced] = useDebouncedValue(searchFilter, 400);
  const previousPage = usePrevious(page) ?? 1;
  // todo: Not sustainable, change later
  const previousSearch = usePrevious(debounced) ?? "";
  const previousStatus = usePrevious(statusFilter) ?? "";
  const previousDoctorFilter = usePrevious(doctorFilter) ?? "";

  const [tablePagination, setTablePagination] = useState<Partial<Pagination>>(
    page > 0
      ? ({
          page,
          pageSize: DEFAULT_PAGINATION.pageSize,
          pageCount: 0,
          total: 0,
        } as Pagination)
      : { ...DEFAULT_PAGINATION }
  );

  useEffect(() => {
    // If there are no query params, use default and replace url in history
    if (!queryParams) {
      import.meta.env.DEV && console.log("No query params, using default");
      setPage(1, "replace");
    }

    // Search term is removed
    if (page > 0 && debounced !== previousSearch && !debounced) {
      import.meta.env.DEV && console.log("Search term removed");
      setPage(1, "replaceIn");
    }

    // If url pagination is different from table pagination, use url pagination
    else if (page > 0 && page !== tablePagination.page) {
      import.meta.env.DEV &&
        console.log("urlPagination !== tablePagination", page);
      setTablePagination(prev => ({ ...prev, page }));
    }

    // Status filter is changed
    else if (previousStatus && statusFilter !== previousStatus) {
      import.meta.env.DEV && console.log("Status filter is changed");
      setPage(1, "replaceIn");
    }

    // Page is loaded with search term,
    else if (
      debounced &&
      tablePagination?.page &&
      tablePagination.page > 1 &&
      previousPage === 1
    ) {
      import.meta.env.DEV && console.log("Page is loaded with search term");
      // Do nothing
      // Return early to prevent the next condition from running and resetting pagination
      return;
    }

    // Search term is set, reset pagination
    else if (debounced && tablePagination?.page && tablePagination.page > 1) {
      import.meta.env.DEV && console.log("Search term is set");
      setPage(1, "replaceIn");
    }
    // Doctor filter is changed
    else if (doctorFilter !== previousDoctorFilter) {
      import.meta.env.DEV && console.log("Doctor filter is changed");
      setPage(1, "replaceIn");
    }

    //* Seems this is not necessary as "status filter is changed" takes over
    // Status filter is set, reset pagination
    // else if (statusFilter) {
    //   import.meta.env.DEV && console.log("Status filter is set");
    //   setPage(1);
    // }
  }, [queryParams, debounced]);

  // useLogger("tablePagination", [{ tablePagination }]);

  return {
    debounced,
    tablePagination,
    setTablePagination,
    previousStatus,
    page,
    setPage,
  };
};
